import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Loader2, Building2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SYM = { USD: "$", NGN: "₦" };

/** Public invoice pay page — no login required. Loaded via /pay/:token */
export default function PublicInvoicePay() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  const paypalRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND}/api/public/invoices/${token}`);
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || "Invoice link is invalid or expired");
      }
      const data = await resp.json();
      setInv(data);
    } catch (err) {
      setError(err.message || "Couldn't load invoice");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Render PayPal button once we have both the invoice AND client id
  useEffect(() => {
    if (!inv || !inv.paypal_available || !inv.paypal_client_id || paid) return;
    if (window.paypal && paypalRef.current && paypalRef.current.childNodes.length === 0) {
      window.paypal.Buttons({
        style: { layout: "vertical", color: "blue", shape: "rect", label: "pay" },
        createOrder: async () => {
          setPaying(true);
          const resp = await fetch(
            `${BACKEND}/api/public/invoices/${token}/paypal-order`,
            { method: "POST" },
          );
          const data = await resp.json();
          setPaying(false);
          if (!resp.ok) { toast.error(data.detail || "Order create failed"); throw new Error(data.detail); }
          return data.order_id;
        },
        onApprove: async (details) => {
          const resp = await fetch(
            `${BACKEND}/api/public/invoices/${token}/paypal-capture/${details.orderID}`,
            { method: "POST" },
          );
          const data = await resp.json();
          if (!resp.ok) { toast.error(data.detail || "Capture failed"); return; }
          toast.success(`Payment received — ${SYM[inv.currency] || ""}${data.captured_amount}`);
          setPaid(true);
          load();
        },
        onError: (err) => toast.error(err?.message || "PayPal error"),
      }).render(paypalRef.current);
    }
  }, [inv, paid, token, load]);

  // Inject PayPal SDK script once we have the client id
  useEffect(() => {
    if (!inv?.paypal_available || !inv?.paypal_client_id) return;
    if (document.getElementById("paypal-sdk-script")) return;
    const s = document.createElement("script");
    s.id = "paypal-sdk-script";
    s.src = `https://www.paypal.com/sdk/js?client-id=${inv.paypal_client_id}&currency=${inv.currency}&intent=capture`;
    s.async = true;
    document.body.appendChild(s);
  }, [inv]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FDFCF8]">
      <div className="max-w-md text-center">
        <AlertCircle className="w-10 h-10 mx-auto text-[#B4283A]" />
        <h1 className="font-display text-2xl mt-4">Link unavailable</h1>
        <p className="text-sm text-[var(--muted-text)] mt-2">{error}</p>
      </div>
    </div>
  );
  if (!inv) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
      <Loader2 className="w-8 h-8 animate-spin text-[var(--muted-text)]" />
    </div>
  );
  const sym = SYM[inv.currency] || "";
  const isPaid = paid || inv.status === "paid" || inv.due <= 0;
  return (
    <div className="min-h-screen bg-[#FDFCF8] p-6" data-testid="public-pay-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <Building2 className="w-5 h-5 text-[#E07A5F]" />
          <div className="font-display text-lg">Accountrix</div>
        </div>
        <div className="card-flat p-8">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted-text)]">Invoice</div>
          <h1 className="font-display text-4xl mt-2" data-testid="pay-invoice-number">{inv.number}</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1">
            Billed to <span className="font-medium text-[var(--text)]">{inv.customer_name}</span> · Due {inv.due_date}
          </p>

          {/* Amount summary */}
          <div className="mt-6 flex items-baseline justify-between border-t border-[var(--divider)] pt-5">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Amount due</div>
              <div className="font-display text-5xl mt-2 font-mono-data" data-testid="pay-amount-due">
                {sym}{inv.due.toLocaleString()}
              </div>
              <div className="text-xs text-[var(--muted-text)] mt-1">
                of {sym}{inv.total.toLocaleString()} total · {sym}{inv.paid.toLocaleString()} paid
              </div>
            </div>
            {isPaid && (
              <div className="inline-flex items-center gap-2 text-green-700 bg-[#EDF3EF] px-3 py-1.5 rounded-full text-sm">
                <CheckCircle2 className="w-4 h-4" /> Paid in full
              </div>
            )}
          </div>

          {/* Line items */}
          <div className="mt-8">
            <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-2">Line items</div>
            <div className="border border-[var(--divider)] rounded-md overflow-hidden">
              {inv.lines?.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_60px_100px_100px] gap-3 p-3 text-sm border-b border-[var(--divider)] last:border-b-0">
                  <div>{l.description}</div>
                  <div className="text-right font-mono-data">{l.quantity}</div>
                  <div className="text-right font-mono-data">{sym}{Number(l.unit_price).toFixed(2)}</div>
                  <div className="text-right font-mono-data font-medium">
                    {sym}{(Number(l.quantity) * Number(l.unit_price)).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pay button */}
          {!isPaid && (
            <div className="mt-8 border-t border-[var(--divider)] pt-6">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-3">Pay now</div>
              {inv.paypal_available ? (
                <div ref={paypalRef} data-testid="paypal-btns-container" />
              ) : (
                <div className="text-sm text-[var(--muted-text)] p-4 border border-[var(--divider)] rounded-md">
                  PayPal isn't set up for this invoice.
                  {inv.currency === "NGN" && " PayPal doesn't support NGN — please pay via bank transfer."}
                </div>
              )}
              {paying && <div className="text-xs text-[var(--muted-text)] mt-2">Creating order…</div>}
            </div>
          )}
          {inv.notes && (
            <div className="mt-6 text-xs text-[var(--muted-text)] border-t border-[var(--divider)] pt-4">
              <div className="uppercase tracking-widest mb-1">Notes</div>
              {inv.notes}
            </div>
          )}
        </div>
        <div className="text-center text-[10px] text-[var(--muted-text)] mt-6">
          Powered by Accountrix · Secure payment via PayPal
        </div>
      </div>
    </div>
  );
}
